/* iter-50p — Bookstore landing thumbnails strip (Phase 1, item 7).
 *
 * Horizontal strip of admin-curated promo tiles for the DESKTOP
 * bookstore landing page. Mirrors `MarketingThumbnailsSidebar` but
 * renders as a wider row instead of a narrow vertical column.
 *
 * Hidden on narrow viewports (mobile bookstore has its own strip).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function BookstoreThumbnailsStrip({ limit = 6 }) {
  const [thumbs, setThumbs] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bookstore/thumbnails?limit=${limit}`)
      .then((r) => { if (alive) setThumbs(r.data?.thumbnails || []); })
      .catch(() => { if (alive) setThumbs([]); });
    return () => { alive = false; };
  }, [limit]);

  if (!thumbs || thumbs.length === 0) return null;

  return (
    <section
      className="mx-auto hidden max-w-6xl px-4 pt-2 sm:block sm:px-6 sm:pt-4"
      data-testid="bs-thumbs-section"
      aria-label="Featured promotions"
    >
      <div className="mb-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Featured</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {thumbs.map((t) => <ThumbTile key={t.id} t={t} />)}
      </div>
    </section>
  );
}

function ThumbTile({ t }) {
  const target = resolveTarget(t);
  const body = (
    <div
      data-testid={`bs-thumb-${t.id}`}
      className="group overflow-hidden rounded-sm border border-border/70 bg-card shadow-sm transition hover:shadow-md"
    >
      {t.image_url && (
        <div className="aspect-[3/2] w-full overflow-hidden bg-[#f4e5c4]">
          <img
            src={t.image_url}
            alt={t.title}
            draggable={false}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
      )}
      <div className="px-2 py-1.5">
        <div className="line-clamp-1 text-[12px] font-semibold text-primary">
          {t.title}
          {t.link_kind === 'external' && (
            <ExternalLink className="ml-1 inline-block h-2.5 w-2.5 opacity-70" />
          )}
        </div>
        {t.caption && (
          <div className="line-clamp-2 text-[10.5px] leading-snug text-muted-foreground">{t.caption}</div>
        )}
      </div>
    </div>
  );

  if (!target) return body;
  if (target.external) {
    return (
      <a href={target.href} target="_blank" rel="noopener noreferrer">{body}</a>
    );
  }
  return <a href={target.href}>{body}</a>;
}

function resolveTarget(t) {
  if (!t) return null;
  if (t.link_kind === 'book'     && t.link_target) return { href: `/bookstore#book=${encodeURIComponent(t.link_target)}` };
  if (t.link_kind === 'genre'    && t.link_target) return { href: `/bookstore/g/${encodeURIComponent(t.link_target)}` };
  if (t.link_kind === 'external' && t.link_target) return { href: t.link_target, external: true };
  return null;
}
